/**
 * The single source of TanStack Query keys. Centralizing them keeps cache
 * invalidation correct and refactor-safe. Each feature adds its namespace here;
 * never inline an array key in a hook.
 */
export const queryKeys = {
  editorDrafts: {
    all: ["local-editor-drafts"] as const,
    image: (spaceID: string, assetID: string) => ["local-editor-drafts", spaceID, assetID] as const,
  },
  mediaTools: {
    catalog: (spaceID: string, category: "image" | "video" | "audio" | "3d") =>
      ["media-tools", spaceID, category] as const,
  },
  me: {
    all: ["me"] as const,
    detail: () => ["me", "detail"] as const,
    preferences: () => ["me", "preferences"] as const,
    authMethods: () => ["me", "auth-methods"] as const,
    passkeys: () => ["me", "passkeys"] as const,
  },
  billing: {
    all: ["billing"] as const,
    usage: () => ["billing", "usage"] as const,
    subscription: () => ["billing", "subscription"] as const,
  },
  foundation: {
    all: ["foundation"] as const,
    account: () => ["foundation", "account"] as const,
    spaces: () => ["foundation", "spaces"] as const,
    assets: (spaceID: string, filters: Record<string, unknown> = {}) =>
      ["foundation", spaceID, "assets", filters] as const,
    asset: (spaceID: string, assetID: string) =>
      ["foundation", spaceID, "assets", assetID] as const,
    assetGraph: (spaceID: string) =>
      ["foundation", spaceID, "assets", "graph"] as const,
    imageEdits: (spaceID: string) =>
      ["foundation", spaceID, "assets", "image-edits"] as const,
    assetGeneration: (spaceID: string, assetID: string) =>
      ["foundation", spaceID, "assets", assetID, "generation"] as const,
    tags: (spaceID: string) => ["foundation", spaceID, "tags"] as const,
    collections: (spaceID: string) => ["foundation", spaceID, "collections"] as const,
    assetShares: (spaceID: string, assetID: string) =>
      ["foundation", spaceID, "assets", assetID, "shares"] as const,
    assetGrants: (spaceID: string, assetID: string) =>
      ["foundation", spaceID, "assets", assetID, "grants"] as const,
    projects: (spaceID: string) => ["foundation", spaceID, "projects"] as const,
    projectCollaborators: (spaceID: string, projectID: string) =>
      ["foundation", spaceID, "projects", projectID, "collaborators"] as const,
    projectContext: (spaceID: string, projectID: string) =>
      ["foundation", spaceID, "projects", projectID, "context"] as const,
    projectDecisions: (spaceID: string, projectID: string) =>
      ["foundation", spaceID, "projects", projectID, "decisions"] as const,
    projectLineage: (spaceID: string, projectID: string) =>
      ["foundation", spaceID, "projects", projectID, "lineage"] as const,
    projectLibrary: (spaceID: string, projectID: string) =>
      ["foundation", spaceID, "projects", projectID, "library"] as const,
    activity: (spaceID: string) => ["foundation", spaceID, "activity"] as const,
    usage: (spaceID: string) => ["foundation", spaceID, "usage"] as const,
    reusable: (spaceID: string, kind: string) =>
      ["foundation", spaceID, "reusable", kind] as const,
    reusableVersions: (spaceID: string, kind: string, reusableID: string) =>
      ["foundation", spaceID, "reusable", kind, reusableID, "versions"] as const,
    reusableAuthorizations: (spaceID: string, kind: string, reusableID: string) =>
      ["foundation", spaceID, "reusable", kind, reusableID, "authorizations"] as const,
    reusableVersion: (spaceID: string, versionID: string) =>
      ["foundation", spaceID, "reusable-versions", versionID] as const,
    recipeVersion: (spaceID: string, versionID: string) =>
      ["foundation", spaceID, "recipe-versions", versionID] as const,
    generationAuthority: (spaceID: string, generationID: string) =>
      ["foundation", spaceID, "generation-authority", generationID] as const,
    members: (spaceID: string) => ["foundation", spaceID, "members"] as const,
    membershipEvents: (spaceID: string) =>
      ["foundation", spaceID, "membership-events"] as const,
    invitations: (spaceID: string) => ["foundation", spaceID, "invitations"] as const,
  },
  projectWorkspace: {
    project: (spaceID: string, projectID: string | null) =>
      ["project-workspace", spaceID, "project", projectID] as const,
  },
  generation: {
    all: ["generation"] as const,
    capabilities: (spaceID: string) => ["generation", spaceID, "capabilities"] as const,
    models: (spaceID: string) => ["generation", spaceID, "models"] as const,
    history: (spaceID: string) => ["generation", spaceID, "history"] as const,
    creationSets: (spaceID: string) => ["generation", spaceID, "creation-sets"] as const,
    detail: (spaceID: string, generationID: string) =>
      ["generation", spaceID, "detail", generationID] as const,
    outputAsset: (spaceID: string, assetID: string) =>
      ["generation", spaceID, "output-asset", assetID] as const,
    assets: (spaceID: string) => ["generation", spaceID, "assets"] as const,
    reusable: (spaceID: string) => ["generation", spaceID, "reusable"] as const,
  },
  audio: {
    all: ["audio"] as const,
    documents: (spaceID: string) => ["audio", spaceID, "documents"] as const,
    document: (spaceID: string, documentID?: string) =>
      ["audio", spaceID, "document", documentID] as const,
    voices: (spaceID: string) => ["audio", spaceID, "voices"] as const,
    voiceLibrary: (spaceID: string) => ["audio", spaceID, "voice-library"] as const,
    asset: (assetID?: string) => ["audio", "asset", assetID] as const,
  },
} as const;
